import { useState, useRef, useEffect, useCallback } from 'react';
import { MapPin, AlertCircle, Loader2 } from 'lucide-react';
import { Input } from './ui/input';

/**
 * Task #2895 — Componente presentazionale condiviso per l'autocomplete
 * indirizzi HERE. Promosso in `ui-frontend` cosi' da essere riusabile da tutti
 * i modali tenant + master senza duplicazione.
 *
 * E' agnostico rispetto al trasporto: il consumer inietta `fetchSuggestions`,
 * che ritorna una union tipizzata. Lo stato `not_configured` (HERE disattivato)
 * fa degradare il campo a input manuale con hint "non configurato", distinto
 * dallo stato `ok` con lista vuota ("Nessun risultato").
 */

export interface AddressResult {
  address?: string;
  city?: string;
  province?: string;
  postal_code?: string;
  country?: string;
}

export interface HereItem {
  title: string;
  address?: {
    label?: string;
    street?: string;
    houseNumber?: string;
    city?: string;
    county?: string;
    state?: string;
    postalCode?: string;
    countryName?: string;
    countryCode?: string;
  };
}

export type AddressFetchResult =
  | { status: 'ok'; items: HereItem[] }
  | { status: 'not_configured' }
  | { status: 'unavailable' }
  | { status: 'error' };

export interface AddressAutocompleteProps {
  onAddressSelect: (address: AddressResult) => void;
  fetchSuggestions: (query: string) => Promise<AddressFetchResult>;
  defaultValue?: string;
  placeholder?: string;
  disabled?: boolean;
  inputClassName?: string;
  /** Etichetta accessibile del campo (quando l'etichetta visibile non è collegata). */
  ariaLabel?: string;
}

// Memoria di processo: se HERE risulta non configurato in una qualsiasi istanza
// della pagina, le altre evitano richieste inutili e mostrano subito l'hint.
let hereProviderUnavailable = false;

export default function AddressAutocomplete({
  onAddressSelect,
  fetchSuggestions,
  defaultValue = '',
  placeholder = 'Cerca indirizzo...',
  disabled = false,
  inputClassName = 'pl-9 pr-9',
  ariaLabel,
}: AddressAutocompleteProps) {
  const [query, setQuery] = useState(defaultValue);
  const [items, setItems] = useState<HereItem[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [hereAvailable, setHereAvailable] = useState(!hereProviderUnavailable);
  const [loading, setLoading] = useState(false);
  const [errorHint, setErrorHint] = useState<string | null>(
    hereProviderUnavailable ? 'Autocomplete indirizzi non configurato (HERE Maps)' : null
  );
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const requestSeqRef = useRef(0);

  useEffect(() => {
    setQuery(defaultValue);
  }, [defaultValue]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const runFetch = useCallback(async (q: string) => {
    if (q.length < 3 || !hereAvailable) {
      setItems([]);
      setShowDropdown(false);
      setLoading(false);
      return;
    }
    const seq = ++requestSeqRef.current;
    setLoading(true);
    try {
      const result = await fetchSuggestions(q);
      // Scarta risposte arrivate fuori ordine.
      if (seq !== requestSeqRef.current) return;
      if (result.status === 'not_configured') {
        hereProviderUnavailable = true;
        setHereAvailable(false);
        setItems([]);
        setShowDropdown(false);
        setErrorHint('Autocomplete indirizzi non configurato (HERE Maps)');
        return;
      }
      if (result.status === 'unavailable') {
        setItems([]);
        setShowDropdown(false);
        setErrorHint('Servizio HERE Maps temporaneamente non disponibile');
        return;
      }
      if (result.status === 'error') {
        setItems([]);
        setShowDropdown(false);
        setErrorHint('Impossibile caricare i suggerimenti');
        return;
      }
      setItems(result.items);
      setShowDropdown(true);
      setErrorHint(result.items.length === 0 ? 'Nessun risultato' : null);
    } finally {
      if (seq === requestSeqRef.current) setLoading(false);
    }
  }, [hereAvailable, fetchSuggestions]);

  const handleInputChange = (value: string) => {
    setQuery(value);
    setErrorHint(null);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    // Invalida sempre eventuali richieste in volo: accorciare/svuotare l'input
    // non deve essere sovrascritto da una risposta vecchia.
    requestSeqRef.current++;
    if (!hereAvailable) {
      setLoading(false);
      return;
    }
    if (value.trim().length < 3) {
      setItems([]);
      setShowDropdown(false);
      setLoading(false);
      return;
    }
    debounceRef.current = setTimeout(() => runFetch(value), 300);
  };

  const handleBlur = () => {
    if (!hereAvailable && query.trim()) {
      onAddressSelect({ address: query.trim() });
    }
  };

  const handleSelect = (item: HereItem) => {
    const addr = item.address;
    const street = addr?.street ? (addr.houseNumber ? `${addr.street}, ${addr.houseNumber}` : addr.street) : item.title;
    setQuery(addr?.label || item.title);
    setShowDropdown(false);
    setItems([]);
    onAddressSelect({
      address: street,
      city: addr?.city || '',
      province: addr?.county || addr?.state || '',
      postal_code: addr?.postalCode || '',
      country: addr?.countryName || 'Italia',
    });
  };

  const effectivePlaceholder = !hereAvailable ? 'Inserisci indirizzo manualmente' : placeholder;

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <Input
          value={query}
          onChange={(e) => handleInputChange(e.target.value)}
          onFocus={() => { if (items.length > 0) setShowDropdown(true); }}
          onBlur={handleBlur}
          placeholder={effectivePlaceholder}
          aria-label={ariaLabel}
          disabled={disabled}
          className={inputClassName}
        />
        {loading && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 animate-spin" />
        )}
      </div>
      {showDropdown && items.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg max-h-48 overflow-y-auto">
          {items.map((item, idx) => (
            <button
              key={idx}
              type="button"
              className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 border-b last:border-b-0 border-gray-100 dark:border-gray-700"
              onClick={() => handleSelect(item)}
            >
              <div className="flex items-center gap-2">
                <MapPin className="w-3 h-3 text-gray-400 flex-shrink-0" />
                <span className="truncate">{item.address?.label || item.title}</span>
              </div>
            </button>
          ))}
        </div>
      )}
      {errorHint && (
        <div className="mt-1 flex items-center gap-1.5 text-[11px] text-amber-600 dark:text-amber-400">
          <AlertCircle className="w-3 h-3 flex-shrink-0" />
          <span>{errorHint}</span>
        </div>
      )}
    </div>
  );
}
